/**
 * Granting and changing a person's role (ADR 0018): a built-in role or one of the organization's roles,
 * over the whole organization or one branch. The server checks every grant again.
 */
import { useState, type FormEvent } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  BranchesResponse,
  ProfileLevel,
  RoleGrant,
  RoleProfilesResponse,
  UserSummary,
} from "@mubin/contracts";
import { ErrorNote, Field, Modal, useSubmit } from "./ui";
import { api, errorText, send } from "../lib/api";
import { roleLabel } from "../lib/labels";
import { useCan, useOrg } from "../lib/org";
import { useMe } from "../lib/session";

export type Choice = { key: string; label: string; role: RoleGrant["role"]; profileId: string | null };

const RANK: Record<RoleGrant["role"], number> = {
  org_admin: 80,
  branch_manager: 60,
  supervisor: 40,
  teacher: 20,
  student: 0,
};

/** The roles the active role may hand out: lower ones, and org_admin by an organization administrator. */
export function useChoices(): Choice[] {
  const org = useOrg();
  const { data: me } = useMe();
  const can = useCan();
  const profiles = useQuery({
    queryKey: ["role-profiles", org?.id],
    queryFn: () => api<RoleProfilesResponse>(`/v1/role-profiles?organizationId=${org!.id}`),
    enabled: !!org && can.has("users.roles"),
  });
  const mine = me?.activeRole && me.activeRole in RANK ? RANK[me.activeRole as RoleGrant["role"]] : 0;
  const allowed = (r: RoleGrant["role"]) =>
    RANK[r] < mine || (r === "org_admin" && me?.activeRole === "org_admin");
  const base: Choice[] = (["org_admin", "branch_manager", "supervisor", "teacher", "student"] as const)
    .filter(allowed)
    .map((r) => ({
      key: r,
      label: r === "teacher" || r === "student" ? roleLabel[r] : `${roleLabel[r]} (بالصلاحيات الافتراضية)`,
      role: r,
      profileId: null,
    }));
  const custom: Choice[] = (profiles.data?.profiles ?? [])
    .filter((p) => !p.archived && allowed(p.level))
    .map((p) => ({ key: p.id, label: p.name, role: p.level, profileId: p.id }));
  return [...custom, ...base];
}

export const isStaff = (r: RoleGrant["role"]): r is ProfileLevel =>
  r === "org_admin" || r === "branch_manager" || r === "supervisor";

/** Grant a new role, or change `grant` (role, scope or profile) in one step. */
export function RoleModal({
  open,
  onClose,
  user,
  grant,
}: {
  open: boolean;
  onClose: () => void;
  user: UserSummary;
  grant?: RoleGrant;
}) {
  const qc = useQueryClient();
  const org = useOrg();
  const choices = useChoices();
  const { busy, error, run } = useSubmit();
  const initial = grant ? (grant.profileId ?? grant.role) : "";
  const [choice, setChoice] = useState(initial);
  const [wide, setWide] = useState(grant ? grant.scopeType === "organization" : false);
  const [branchId, setBranchId] = useState(grant?.scopeType === "branch" ? (grant.scopeId ?? "") : "");
  const branches = useQuery({
    queryKey: ["branches"],
    queryFn: () => api<BranchesResponse>("/v1/branches"),
    enabled: open,
  });
  const picked = choices.find((c) => c.key === choice);
  // Teachers and students hang on circles and enrollments; changing them is done by grant and revoke.
  const options = grant
    ? choices.filter((c) => isStaff(c.role) === isStaff(grant.role) || c.role === grant.role)
    : choices;
  const staff = picked ? isStaff(picked.role) : false;
  const orgWide = picked?.role === "org_admin" || (staff && wide);
  // Teachers are granted in a branch (their circles give them their reach); staff in a branch or everywhere.
  const needsBranch = picked?.role === "teacher" || (staff && !orgWide);

  function submit(e: FormEvent) {
    e.preventDefault();
    if (!picked || !org) return;
    void run(async () => {
      const scope =
        picked.role === "student"
          ? { scopeType: "self" as const, scopeId: null }
          : orgWide
            ? { scopeType: "organization" as const, scopeId: org.id }
            : { scopeType: "branch" as const, scopeId: branchId };
      if (grant) {
        await send("PATCH", `/v1/role-assignments/${grant.assignmentId}`, {
          role: picked.role,
          ...scope,
          profileId: picked.profileId,
        });
      } else {
        await send("POST", `/v1/users/${user.id}/roles`, {
          organizationId: org.id,
          role: picked.role,
          ...scope,
          ...(picked.profileId && { profileId: picked.profileId }),
        });
      }
      await Promise.all(["users", "circles", "overview"].map((k) => qc.invalidateQueries({ queryKey: [k] })));
      onClose();
    }, errorText);
  }

  return (
    <Modal title={grant ? `تعديل دور ${user.name}` : `منح دور لـ${user.name}`} open={open} onClose={onClose}>
      <form className="form" onSubmit={submit}>
        <ErrorNote error={error} />
        <Field id="g-role" label="الدور">
          <select id="g-role" value={choice} onChange={(e) => setChoice(e.target.value)} required>
            <option value="">اختر الدور</option>
            {options.map((c) => (
              <option key={c.key} value={c.key}>
                {c.label}
              </option>
            ))}
          </select>
        </Field>
        {staff && picked?.role !== "org_admin" && (
          <Field id="g-scope" label="النطاق">
            <div className="chips" role="radiogroup">
              <label className="check">
                <input type="radio" checked={wide} onChange={() => setWide(true)} />
                الجمعية كلها (كل المجمعات)
              </label>
              <label className="check">
                <input type="radio" checked={!wide} onChange={() => setWide(false)} />
                مجمع واحد
              </label>
            </div>
          </Field>
        )}
        {needsBranch && (
          <Field id="g-branch" label="المجمع">
            <select id="g-branch" value={branchId} onChange={(e) => setBranchId(e.target.value)} required>
              <option value="">اختر المجمع</option>
              {branches.data?.branches
                .filter((b) => !b.archivedAt)
                .map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                  </option>
                ))}
            </select>
          </Field>
        )}
        <div className="form-actions">
          <button className="btn btn-primary" disabled={busy || !picked || (needsBranch && !branchId)}>
            {busy ? "جارٍ الحفظ…" : grant ? "حفظ التعديل" : "منح الدور"}
          </button>
        </div>
      </form>
    </Modal>
  );
}
